if (!customElements.get("fourpaw-gift-personalization")) {
  class FourPawGiftPersonalization extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.toggleButton = this.querySelector("[data-fourpaw-gift-personalization-toggle]");
      this.panel = this.querySelector("[data-fourpaw-gift-personalization-panel]");
      this.nameInput = this.querySelector("[data-fourpaw-gift-personalization-name]");
      this.phoneInput = this.querySelector("[data-fourpaw-gift-personalization-phone]");
      this.saveButton = this.querySelector("[data-fourpaw-gift-personalization-save]");
      this.cancelButton = this.querySelector("[data-fourpaw-gift-personalization-cancel]");
      this.status = this.querySelector("[data-fourpaw-gift-personalization-status]");
      this.toggleLabel = this.querySelector("[data-fourpaw-gift-personalization-toggle-label]");
      this.freeBadge = this.querySelector("[data-fourpaw-gift-personalization-free]");
      this.summary = this.querySelector("[data-fourpaw-gift-personalization-summary]");
      this.summaryName = this.querySelector("[data-fourpaw-gift-personalization-summary-name]");
      this.summaryPhone = this.querySelector("[data-fourpaw-gift-personalization-summary-phone]");
      this.initialName = this.nameInput?.value || "";
      this.initialPhone = this.phoneInput?.value || "";

      this.onToggle = () => this.setOpen(this.panel?.hidden !== false);
      this.onCancel = () => this.cancel();
      this.onSave = () => this.save();
      this.onInputKeydown = (event) => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        this.save();
      };
      this.toggleButton?.addEventListener("click", this.onToggle);
      this.cancelButton?.addEventListener("click", this.onCancel);
      this.saveButton?.addEventListener("click", this.onSave);
      [this.nameInput, this.phoneInput].forEach((input) => {
        input?.addEventListener("keydown", this.onInputKeydown);
      });
    }

    disconnectedCallback() {
      this.toggleButton?.removeEventListener("click", this.onToggle);
      this.cancelButton?.removeEventListener("click", this.onCancel);
      this.saveButton?.removeEventListener("click", this.onSave);
      [this.nameInput, this.phoneInput].forEach((input) => {
        input?.removeEventListener("keydown", this.onInputKeydown);
      });
      this.initialized = false;
    }

    setOpen(open) {
      if (!this.panel || !this.toggleButton) return;
      this.panel.hidden = !open;
      this.toggleButton.setAttribute("aria-expanded", String(open));
      if (open) this.nameInput?.focus();
    }

    cancel() {
      if (this.nameInput) this.nameInput.value = this.initialName;
      if (this.phoneInput) this.phoneInput.value = this.initialPhone;
      if (this.status) this.status.textContent = "";
      this.setOpen(false);
    }

    updatedLineItem(cart) {
      const variantId = Number(this.dataset.variantId || 0);
      const packageId = this.dataset.packageId || "";
      if (!Array.isArray(cart?.items)) return null;

      const exact = cart.items.find((item) => item.key === this.dataset.lineKey && Number(item.variant_id) === variantId);
      if (exact) return exact;
      if (!packageId) return null;
      const matches = cart.items.filter((item) => Number(item.variant_id) === variantId &&
        String(item.properties?.["_Package ID"] || "") === packageId);
      return matches.length === 1 ? matches[0] : null;
    }

    updatePresentation(dogName, phoneNumber, properties, lineItem) {
      const hasPersonalization = Boolean(dogName || phoneNumber);

      this.initialName = dogName;
      this.initialPhone = phoneNumber;
      if (this.nameInput) this.nameInput.value = dogName;
      if (this.phoneInput) this.phoneInput.value = phoneNumber;
      if (this.toggleLabel) {
        this.toggleLabel.textContent = hasPersonalization
          ? this.dataset.editLabel || "Edit name + phone"
          : this.dataset.addLabel || "Personalize your tags — free";
      }

      if (this.freeBadge) this.freeBadge.hidden = !hasPersonalization;
      if (this.summary) this.summary.hidden = !hasPersonalization;
      if (this.summaryName) {
        this.summaryName.textContent = dogName;
        this.summaryName.parentElement.hidden = !dogName;
      }
      if (this.summaryPhone) {
        this.summaryPhone.textContent = phoneNumber;
        this.summaryPhone.parentElement.hidden = !phoneNumber;
      }

      this.classList.toggle("is-personalized", hasPersonalization);
      if (lineItem?.key) this.dataset.lineKey = lineItem.key;
      if (lineItem?.quantity) this.dataset.quantity = String(lineItem.quantity);
      const row = this.closest(".cart-item");
      if (row && lineItem) {
        row.dataset.lineKey = String(lineItem.key || row.dataset.lineKey || "");
        row.dataset.lineQuantity = String(lineItem.quantity || 0);
        row.dataset.lineFinalPrice = String(lineItem.final_line_price || 0);
      }
    }

    async save() {
      if (!this.saveButton || this.saveButton.disabled) return;

      const dogName = (this.nameInput?.value || "").trim();
      const phoneNumber = (this.phoneInput?.value || "").trim();
      if (!dogName && !phoneNumber) {
        if (this.status) this.status.textContent = this.dataset.requiredLabel || "Enter a name, a phone number, or both.";
        this.nameInput?.focus();
        return;
      }

      const originalLabel = this.saveButton.textContent.trim();
      await window.fourPawCartWork?.settled;
      const finish = window.fourPawCartWork?.begin();
      if (window.fourPawCartWork && !finish) return;
      this.saveButton.disabled = true;
      this.saveButton.textContent = this.dataset.savingLabel || "Saving…";
      if (this.status) this.status.textContent = "";

      try {
        const root = window.Shopify?.routes?.root || "/";
        const request = window.fourPawCartFetch || window.fetch.bind(window);
        const currentResponse = await request(`${root}cart.js`, {
          headers: { Accept: "application/json" }, cache: "no-store",
        });
        if (!currentResponse.ok) throw new Error("Unable to read current personalization.");
        const current = this.updatedLineItem(await currentResponse.json());
        if (!current) throw new Error("This gift pack changed. Please reopen the cart and try again.");
        const properties = { ...(current.properties || {}) };
        delete properties["Gift personalization"];
        if (dogName) properties["Dog name"] = dogName;
        else delete properties["Dog name"];
        if (phoneNumber) properties["Phone number"] = phoneNumber;
        else delete properties["Phone number"];
        const response = await request(`${root}cart/change.js`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            Accept: "application/json",
          },
          body: JSON.stringify({
            id: current.key,
            quantity: current.quantity,
            properties,
          }),
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || "Unable to save personalization.");
        }

        document.querySelectorAll("fourpaw-gift-personalization").forEach((editor) => {
          const lineItem = editor.updatedLineItem(data);
          if (!lineItem) return;
          const savedProperties = lineItem.properties || {};
          editor.updatePresentation(savedProperties["Dog name"] || "", savedProperties["Phone number"] || "", savedProperties, lineItem);
        });
        const controller = document.querySelector("cart-drawer-items, cart-items");
        if (controller?.normalizeFourPawSafeWalkCart) {
          const normalized = await controller.normalizeFourPawSafeWalkCart(data);
          await controller.fourPawRefreshCart(normalized.cart, true);
        }
        this.saveButton.disabled = false;
        this.saveButton.textContent = originalLabel;
        this.setOpen(false);
        this.classList.add("is-saved");
        if (this.status) this.status.textContent = this.dataset.savedLabel || "Saved";
        document.dispatchEvent(
          new CustomEvent("fourpaw:gift-personalization-saved", {
            detail: { hasName: Boolean(dogName), hasPhone: Boolean(phoneNumber) },
          }),
        );
        window.setTimeout(() => {
          if (!this.isConnected) return;
          this.classList.remove("is-saved");
          if (this.status) this.status.textContent = "";
        }, 1600);
      } catch (error) {
        window.fourPawCartWork?.fail();
        this.classList.remove("is-saved");
        this.saveButton.disabled = false;
        this.saveButton.textContent = originalLabel;
        if (this.status) {
          this.status.textContent = this.dataset.errorLabel || "Could not save. Please try again.";
        }
        this.dataset.errorDetail = error instanceof Error ? error.message : String(error);
      } finally {
        finish?.();
      }
    }
  }

  customElements.define("fourpaw-gift-personalization", FourPawGiftPersonalization);
}
